import React, { useEffect, useState, useRef } from 'react';
import Peer from 'peerjs';
import './styles.css';

interface SetlistData {
  now: string;
  reserve: string;
  log: string;
}

const SetlistBrowser: React.FC = () => {
  const [peerKey, setPeerKey] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [setlistData, setSetlistData] = useState<SetlistData>({
    now: '',
    reserve: '',
    log: '',
  });
  const [relativeTime, setRelativeTime] = useState<string>('');
  const [selectedSkin, setSelectedSkin] = useState<string>('simpleBlackA');
  const peerRef = useRef<Peer>();
  const connectionRef = useRef<any>();

  const skins = [
    'simpleBlackA',
    'simpleBlackB',
    'simpleWhiteA',
    'simpleWhiteB',
    'textOnly',
    'soulLike',
    'nightTime',
    'mini',
    'singlePlateWhite',
    'starBirth',
    'kienaiStarBirth',
    'sign',
    'utaNyankoBlack',
    'utaNyankoWhite',
    'utaNyankoGaming',
    'kadomaru',
    'guruguru',
    'ranking',
    'label',
    'tag',
    'osngkMin',
    'osngkFude',
    'counter',
    'counterBlack',
    'trainTrain',
    'mobileDoll',
    'pager',
    'shirowaku',
    'checkers',
    'tatelista',
    'tatelistaR',
    'tatelistaAtsu',
    'ichiran',
    'forMizzo',
  ];

  useEffect(() => {
    // Initialize PeerJS
    const peer = new Peer();
    peerRef.current = peer;

    // Load saved data from localStorage
    loadData();

    return () => {
      peer.destroy();
    };
  }, []);

  const loadData = () => {
    const savedNow = localStorage.getItem('setlistaNow') || '';
    const savedReserve = localStorage.getItem('setlistaRes') || '';
    const savedLog = localStorage.getItem('setlistaLog') || '';

    setSetlistData({
      now: savedNow,
      reserve: savedReserve,
      log: savedLog,
    });
  };

  const saveData = () => {
    localStorage.setItem('setlistaNow', setlistData.now);
    localStorage.setItem('setlistaRes', setlistData.reserve);
    localStorage.setItem('setlistaLog', setlistData.log);
  };

  const tsumetsume = (txt: string): string => {
    txt = txt.trim();
    txt = txt.replace(/(\r?\n)+/g, '\n');
    return txt;
  };

  const addTime = () => {
    if (relativeTime) {
      const d = new Date();
      const nowS = d.getTime() - parseInt(relativeTime);
      const sec = Math.floor(nowS / 1000) % 60;
      const min = Math.floor(nowS / 1000 / 60) % 60;
      const hours = Math.floor(nowS / 1000 / 60 / 60) % 24;
      let stamp = '';
      if (hours > 0) {
        stamp = hours + ':';
      }
      stamp = stamp + ('00' + min).slice(-2) + ':' + ('00' + sec).slice(-2);
      stamp = ' - [' + stamp + ']';

      setSetlistData((prev) => ({
        ...prev,
        now: prev.now + stamp,
      }));
    }
  };

  const setRelative = () => {
    const d = new Date();
    setRelativeTime(d.getTime().toString());
  };

  const clearRelativeTime = () => {
    setRelativeTime('');
  };

  const getOutputPeer = () => {
    if (!peerKey) return;

    const conn = peerRef.current!.connect(peerKey);
    conn.on('open', function () {
      conn.send(['connect', 'Connected successfully.']);
      setIsConnected(true);
      connectionRef.current = conn;
    });
  };

  const sendList = () => {
    if (!connectionRef.current) return;

    const data = [
      encodeURI(setlistData.now),
      encodeURI(setlistData.reserve),
      encodeURI(setlistData.log),
    ];
    connectionRef.current.send(['sync', data]);
  };

  const handleSkinChange = (skin: string) => {
    setSelectedSkin(skin);
    if (connectionRef.current) {
      connectionRef.current.send(['skin', skin]);
    }
  };

  const editList = () => {
    // Check if data has changed and save if needed
    const savedNow = localStorage.getItem('setlistaNow') || '';
    const savedReserve = localStorage.getItem('setlistaRes') || '';
    const savedLog = localStorage.getItem('setlistaLog') || '';

    if (
      setlistData.now !== savedNow ||
      setlistData.reserve !== savedReserve ||
      setlistData.log !== savedLog
    ) {
      const cleanedData = {
        now: tsumetsume(setlistData.now),
        reserve: tsumetsume(setlistData.reserve),
        log: tsumetsume(setlistData.log),
      };
      setSetlistData(cleanedData);
      saveData();
      sendList();
      return;
    }

    // If now field has text, move it to log
    if (setlistData.now !== '') {
      const newLog =
        setlistData.log + (setlistData.log ? '\n' : '') + setlistData.now;
      const newData = {
        now: '',
        reserve: setlistData.reserve,
        log: newLog,
      };
      setSetlistData(newData);
    } else {
      // If reserve has content, move first item to now
      if (setlistData.reserve !== '') {
        const reserveLines = setlistData.reserve.split('\n');
        const first = reserveLines[0];
        const remaining = reserveLines.slice(1).join('\n');

        const newData = {
          now: first,
          reserve: remaining,
          log: setlistData.log,
        };

        addTime();
        setSetlistData(newData);
      }
    }

    // Clean up whitespace
    const cleanedData = {
      now: tsumetsume(setlistData.now),
      reserve: tsumetsume(setlistData.reserve),
      log: tsumetsume(setlistData.log),
    };

    setSetlistData(cleanedData);
    saveData();
    sendList();
  };

  const handleInputChange = (field: keyof SetlistData, value: string) => {
    setSetlistData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="setlist-browser">
      <h1>SETLISTA CONTROL</h1>

      <div className="control">
        <div>
          <h2>Reserve List</h2>
          <p>Enter song names to sing, one song per line.</p>
          <textarea
            value={setlistData.reserve}
            onChange={(e) => handleInputChange('reserve', e.target.value)}
            placeholder="Enter songs to sing..."
          />
        </div>

        <div>
          <h2>Now Singing</h2>
          <input
            value={setlistData.now}
            onChange={(e) => handleInputChange('now', e.target.value)}
            placeholder="Current song..."
          />
        </div>

        <div>
          <h2>Set List</h2>
          <p>
            This will be the list of songs that have been performed. You can
            also edit this directly.
          </p>
          <textarea
            value={setlistData.log}
            onChange={(e) => handleInputChange('log', e.target.value)}
            placeholder="Songs already performed..."
          />
        </div>

        <button id="ctlSync" onClick={editList}>
          ▶︎
        </button>
      </div>

      <div className="setting">
        <div>
          <h2>Key</h2>
          <p>Enter the connection key displayed on the OBS side</p>
          <input
            value={peerKey}
            onChange={(e) => setPeerKey(e.target.value)}
            placeholder="Enter peer key..."
          />
          <button onClick={getOutputPeer}>Connect</button>
        </div>

        <div>
          <h2>Skin</h2>
          <select
            value={selectedSkin}
            onChange={(e) => handleSkinChange(e.target.value)}
          >
            <option value="">Change Skin</option>
            {skins.map((skin) => (
              <option key={skin} value={skin}>
                {skin}
              </option>
            ))}
          </select>
        </div>

        <div>
          <h2>Time Stamp</h2>
          <p>Feature under testing</p>
          <input
            value={relativeTime}
            onChange={(e) => setRelativeTime(e.target.value)}
            disabled
            placeholder="Relative time..."
          />
          <button onClick={setRelative}>Set current time as reference</button>
          <button onClick={clearRelativeTime}>CLEAR</button>
        </div>
      </div>
    </div>
  );
};

export default SetlistBrowser;
